import {
  type CohortT,
  createCohortFromRecords,
  computeCohortStateAtDate,
} from '@ruminati/livestock-inventory/cohort'
import {
  LivestockCohortCreateT,
  LivestockCohortRecordT,
  type LivestockCohortT,
  type LivestockCohortUpdateT,
} from '@ruminati/types_v2/livestock_cohorts'
import {
  LivestockEventCreateT,
  type LivestockEventT,
  type LivestockEventUpdateT,
} from '@ruminati/types_v2/livestock_events'
import {
  type LivestockOnboardingT,
  type LivestockOnboardingResponseT,
  type LivestockCohortCreateWithEventDetailsT,
  type LivestockOnboardingCohortResponseT,
} from '@ruminati/types_v2/livestock_onboarding'
import {
  type LivestockCohortSplitCreateT,
  type LivestockCohortSplitResponseT,
} from '@ruminati/types_v2/livestock_split'
import {
  CattleBreedConfigT,
  LivestockSystemT,
  LivestockSystemUpdateT,
  SheepBreedConfigT,
} from '@ruminati/types_v2/livestock_system'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { CommonApiV2Instance } from '@/services/common'

import { useOrganisationStore } from './organisations'
import { usePropertiesStore } from './properties'

export const useLivestockStore = defineStore('livestock', () => {
  const organisationStore = useOrganisationStore()
  const propertiesStore = usePropertiesStore()

  const showingDemoData = ref(true)

  const cohorts = ref<Array<LivestockCohortT> | undefined>()
  const events = ref<Array<LivestockEventT> | undefined>()
  const livestockSystemDetails = ref<Array<LivestockSystemT> | undefined>()

  function setCohorts(newCohorts: Array<LivestockCohortT>) {
    cohorts.value = newCohorts
  }

  function setEvents(newEvents: Array<LivestockEventT>) {
    events.value = newEvents
  }

  function setSystemDetails(newDetails: Array<LivestockSystemT>) {
    livestockSystemDetails.value = newDetails
  }

  function resetAll() {
    cohorts.value = undefined
    events.value = undefined
    livestockSystemDetails.value = undefined
    showingDemoData.value = true
  }

  async function updateSystemDetails(recordId: string, body: LivestockSystemUpdateT) {
    const data = await CommonApiV2Instance.patch(
      `organisations/${organisationStore.currentOrg?.id}/livestock-system-details/${recordId}`,
      { json: body },
    ).json<LivestockSystemT>()
    if (data) {
      const idx = (livestockSystemDetails.value ?? []).findIndex((c) => c.id === recordId)
      if (idx !== -1) {
        livestockSystemDetails.value?.splice(idx, 1, data)
      }
    }
  }

  async function createEvent(body: LivestockEventCreateT): Promise<LivestockEventT | undefined> {
    const propertyId = body.propertyId ?? propertiesStore.properties?.[0]?.id
    if (!propertyId) return
    const data = await CommonApiV2Instance.post(`properties/${propertyId}/livestock-events`, {
      json: body,
    }).json<LivestockEventT>()
    if (data) {
      if (!events.value) events.value = []
      events.value.push(data)
    }
    return data
  }

  async function updateEvent(data: {
    propertyId: string
    eventId: string
    body: LivestockEventUpdateT
  }): Promise<LivestockEventT> {
    const responseData = await CommonApiV2Instance.patch(
      `properties/${data.propertyId}/livestock-events/${data.eventId}`,
      { json: data.body },
    ).json<LivestockEventT>()
    if (events.value) {
      const idx = events.value.findIndex((e) => e.id === data.eventId)
      if (idx > -1) events.value.splice(idx, 1, responseData)
    }
    return responseData
  }

  async function deleteEvent(data: { propertyId: string; eventId: string }): Promise<void> {
    const response = await CommonApiV2Instance.delete(
      `properties/${data.propertyId}/livestock-events/${data.eventId}`,
      { headers: { 'content-type': undefined } },
    )
    if (response.status === 204 && events.value) {
      const idx = events.value.findIndex((e) => e.id === data.eventId)
      if (idx > -1) events.value.splice(idx, 1)
    }
  }

  async function submitOnboardingData(propertyId: string, data: LivestockOnboardingT) {
    const responseData = await CommonApiV2Instance.post(
      `properties/${propertyId}/livestock-onboarding`,
      { json: data },
    ).json<LivestockOnboardingResponseT>()
    if (!cohorts.value) cohorts.value = []
    cohorts.value.push(...responseData.cohorts)
    if (!events.value) events.value = []
    events.value.push(...responseData.events)
    if (!livestockSystemDetails.value) livestockSystemDetails.value = []
    const systemIndex = livestockSystemDetails.value.findIndex(
      (system) => system.propertyId === responseData.systemDetails.propertyId,
    )
    if (systemIndex === -1) {
      livestockSystemDetails.value.push(responseData.systemDetails)
    } else {
      livestockSystemDetails.value.splice(systemIndex, 1, responseData.systemDetails)
    }
  }

  async function submitOnboardingDataSingleCohort(
    propertyId: string,
    data: LivestockCohortCreateWithEventDetailsT,
  ) {
    const responseData = await CommonApiV2Instance.post(
      `properties/${propertyId}/livestock-onboarding-cohort`,
      { json: data },
    ).json<LivestockOnboardingCohortResponseT>()
    if (!cohorts.value) cohorts.value = []
    cohorts.value.push(responseData.cohort)
    if (!events.value) events.value = []
    events.value.push(...responseData.events)
  }

  async function createCohort(data: LivestockCohortCreateT): Promise<LivestockCohortRecordT> {
    const responseData = await CommonApiV2Instance.post(
      `properties/${data.propertyId}/livestock-cohorts`,
      { json: data },
    ).json<LivestockCohortRecordT>()
    if (!cohorts.value) cohorts.value = []
    cohorts.value.push(responseData)
    return responseData
  }

  async function deleteCohort(data: { propertyId: string; cohortId: string }) {
    const response = await CommonApiV2Instance.delete(
      `properties/${data.propertyId}/livestock-cohorts/${data.cohortId}`,
      { headers: { 'content-type': undefined } },
    )
    if (response.status === 204 && cohorts.value) {
      const idx = cohorts.value.findIndex((c) => c.id === data.cohortId)
      if (idx > -1) cohorts.value.splice(idx, 1)
    }
  }

  async function updateCohort(data: {
    propertyId: string
    cohortId: string
    body: LivestockCohortUpdateT
  }): Promise<LivestockCohortRecordT> {
    const responseData = await CommonApiV2Instance.patch(
      `properties/${data.propertyId}/livestock-cohorts/${data.cohortId}`,
      { json: data.body },
    ).json<LivestockCohortRecordT>()
    if (cohorts.value) {
      const idx = cohorts.value.findIndex((c) => c.id === data.cohortId)
      if (idx > -1) cohorts.value.splice(idx, 1, responseData)
    }
    return responseData
  }

  async function splitCohort(
    sourceCohortId: string,
    propertyId: string,
    data: LivestockCohortSplitCreateT,
  ): Promise<LivestockCohortSplitResponseT | undefined> {
    const responseData = await CommonApiV2Instance.post(
      `properties/${propertyId}/livestock-cohorts/${sourceCohortId}/split`,
      { json: data },
    ).json<LivestockCohortSplitResponseT>()
    if (!cohorts.value) cohorts.value = []
    cohorts.value.push(...responseData.cohorts)
    if (!events.value) events.value = []
    events.value.push(responseData.event)
    return responseData
  }

  const cohortStates = computed<CohortT[]>(() => {
    return (
      showingDemoData.value && cohorts.value
        ? cohorts.value.map((c) => {
            const systemDetails: SheepBreedConfigT | CattleBreedConfigT = c.metadata?.configuration
            if (!systemDetails) return undefined
            return createCohortFromRecords({
              cohortRecord: c,
              cohortEvents:
                events.value?.filter((e) => e.details.some((d) => d.cohortId === c.id)) ?? [],
              systemDetails,
            })
          })
        : []
    ).filter((c) => c !== undefined)
  })

  const cohortStateToday = computed(() => {
    return cohortStates.value.map((c) => computeCohortStateAtDate({ cohort: c, date: new Date() }))
  })

  function toggleDemoMode(enabled: boolean) {
    showingDemoData.value = enabled
  }

  function completeOnboarding(payload: { cohorts: LivestockCohortT[]; events: LivestockEventT[] }) {
    cohorts.value = payload.cohorts
    events.value = payload.events
    showingDemoData.value = true
  }

  return {
    cohorts,
    events,
    livestockSystemDetails,
    showingDemoData,

    setCohorts,
    setEvents,
    setSystemDetails,
    resetAll,

    cohortStates,
    cohortStateToday,
    toggleDemoMode,
    completeOnboarding,

    createCohort,
    updateCohort,
    deleteCohort,
    splitCohort,

    createEvent,
    updateEvent,
    deleteEvent,

    updateSystemDetails,
    submitOnboardingData,
    submitOnboardingDataSingleCohort,
  }
})

export default useLivestockStore
